import axios, { AxiosHeaderValue, AxiosHeaders, AxiosRequestConfig, AxiosResponseHeaders, HttpStatusCode, Method } from 'axios';
import handleError from './error';
import { ENodeEnv } from '../store/enum';

export interface IRequest<T = any> {
    url: string;
    baseURL: string;
    data?: T;
}

export interface IResponse<D = any> {
    data: D;
    status: number;
    statusText: string;
    headers: TResponseHeaders;
    config: IRequestConfig;
    request?: any;
}

export interface IPaginatedResponse<D> {
    value: D[];
    pageNumber: number;
    pageSize: number;
    pageCount: number;
}

export interface IPaginatedRequest {
    pageNumber?: number;
    pageSize?: number;
    name?: string;
}

export type TRequestHeaders = Record<string, AxiosHeaderValue | AxiosHeaders>;

interface IRequestConfig extends AxiosRequestConfig {}

type TResponseHeaders = AxiosResponseHeaders;

type TMethod = Method;

const httpClient = () => {
    const _instance = axios.create(process.env.NODE_ENV == ENodeEnv.Production ? { withCredentials: true } : {});

    const _createHeaders = (headers?: TRequestHeaders) => {
        return {
            'Content-Type': headers?.contentType || 'application/json',
            ...headers,
        };
    };

    const _createRequest = <T = any>(method: TMethod, request: IRequest<T>, headers?: TRequestHeaders): AxiosRequestConfig<T> => {
        return {
            method: method,
            baseURL: request.baseURL,
            url: request.url,
            data: request.data,
            headers: _createHeaders(headers),
        };
    };

    _instance.interceptors.response.use(
        res => res,
        error => {
            handleError(error.response.status);
            // Do something with response error
            return Promise.reject(error);
        },
    );

    const getAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('get', request, headers));
    };

    const patchAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('patch', request, headers));
    };

    const postAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('post', request, headers));
    };

    const putAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('put', request, headers));
    };

    const deleteAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('delete', request, headers));
    };

    return {
        getAsync,
        patchAsync,
        postAsync,
        putAsync,
        deleteAsync,
    };
};

export default httpClient();
